import { useEffect, useState } from 'react';

const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/5018b99a-47b8-4d57-8d92-fd3feab4bd47.png" 
              alt="STELLA SH.P.K. Logo" 
              className="h-10 w-10 object-contain"
            />
            <span className="text-2xl font-light tracking-tight text-gray-900">STELLA SH.P.K.</span>
          </div>
          <div className="hidden md:flex items-center gap-12">
            <button onClick={() => scrollToSection('home')} className="text-sm font-light tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-sm font-light tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-sm font-light tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('features')} className="text-sm font-light tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Features</button>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-light tracking-wider text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-32 pb-24 px-8 bg-gradient-to-br from-slate-50 via-gray-50 to-stone-50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column */}
            <div className="lg:col-span-5">
              <h1 className="text-6xl lg:text-7xl font-light leading-tight text-gray-900 mb-6">
                Crafting Premium<br />
                <span className="inline-flex items-center gap-3">
                  <i className="ri-smartphone-line text-5xl"></i>
                  <span>iOS</span>
                </span><br />
                Experiences
              </h1>
              <p className="text-lg font-light text-gray-600 leading-relaxed mb-8">
                for Modern Mobile Users
              </p>
              <p className="text-base text-gray-500 leading-loose max-w-md">
                We are a team of experts developing modern applications for the iOS platform, designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>

            {/* Center Column - iPhone Mockup */}
            <div className="lg:col-span-4 flex justify-center">
              <div className="relative transform lg:rotate-6 transition-transform hover:rotate-0 duration-500">
                <div className="w-64 h-auto">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/a53e28992dfa35865dcd19f9559b34f0.jpg" 
                    alt="VPN App Interface"
                    className="w-full h-auto object-contain drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Feature Cards */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-3xl text-gray-700"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure Protection</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Advanced encryption keeps your data safe and private</p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <i className="ri-speed-line text-3xl text-gray-700"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Lightning Fast</h3>
                <p className="text-sm text-gray-600 leading-relaxed">Optimized performance for seamless experience</p>
              </div>
              <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-3xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <i className="ri-lock-line text-3xl text-gray-700"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy First</h3>
                <p className="text-sm text-gray-600 leading-relaxed">No logs, no tracking, complete anonymity</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block px-6 py-2 border border-gray-300 rounded-full mb-8">
            <span className="text-xs uppercase tracking-widest text-gray-600">Who We Are</span>
          </div>
          <h2 className="text-5xl font-light leading-tight text-gray-900 mb-12">
            <strong className="font-semibold">STELLA SH.P.K.</strong> is a team of <span className="inline-flex items-baseline gap-2"><i className="ri-team-line text-4xl"></i>experts</span> developing modern <span className="inline-flex items-baseline gap-2"><i className="ri-apps-line text-4xl"></i>applications</span> for the iOS platform
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We combine cutting-edge technology with intuitive design to deliver applications that users trust and love.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 px-8 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <p className="text-sm font-light text-gray-500 mb-2">Our Solutions</p>
            <h2 className="text-6xl font-bold text-gray-900">VPN & Cleaner</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* VPN Card */}
            <div className="bg-gray-100 rounded-3xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-shield-line text-4xl text-gray-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Secure VPN</h3>
              <ul className="space-y-4 text-base text-gray-600 leading-relaxed">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Military-grade encryption for complete data protection</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Strict no-logs policy ensures your privacy</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Global server network for optimal connectivity</span>
                </li>
              </ul>
            </div>

            {/* Cleaner Card */}
            <div className="bg-gray-100 rounded-3xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-brush-line text-4xl text-gray-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Smart Cleaner</h3>
              <ul className="space-y-4 text-base text-gray-600 leading-relaxed">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Intelligent storage optimization and management</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Remove junk files and free up valuable space</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Boost device performance and battery life</span>
                </li>
              </ul>
            </div>

            {/* Technology Card */}
            <div className="bg-gray-100 rounded-3xl p-10 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className="ri-cpu-line text-4xl text-gray-800"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Advanced Tech</h3>
              <ul className="space-y-4 text-base text-gray-600 leading-relaxed">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Built with Swift for native iOS performance</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Leveraging Core frameworks for reliability</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-gray-800 mt-1"></i>
                  <span>Continuous optimization for latest iOS versions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column */}
            <div>
              <h2 className="text-5xl font-light leading-tight text-gray-900 mb-8">
                Designed for<br />iOS Excellence
              </h2>
              <p className="text-lg text-gray-600 leading-loose">
                Our development philosophy centers on creating applications that integrate seamlessly with the iOS ecosystem. Every feature is carefully crafted to provide an intuitive, native experience that feels right at home on your iPhone or iPad. We prioritize performance, security, and user satisfaction in every line of code we write.
              </p>
            </div>

            {/* Right Column - Feature Images Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1 relative rounded-2xl overflow-hidden h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20VPN%20connection%20screen%20interface%20showing%20secure%20connection%20status%20with%20blue%20gradient%20background%20and%20connection%20button%20clean%20iOS%20app%20design%20minimalist%20style&width=600&height=800&seq=feature-vpn-connection-001&orientation=portrait" 
                  alt="VPN Connection"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-white rounded-lg px-4 py-2">
                  <span className="text-sm font-medium text-gray-900">VPN Connection</span>
                </div>
              </div>
              <div className="col-span-2 md:col-span-1 relative rounded-2xl overflow-hidden h-64">
                <img 
                  src="https://readdy.ai/api/search-image?query=speed%20test%20results%20display%20showing%20fast%20internet%20connection%20metrics%20with%20circular%20speedometer%20gauge%20modern%20iOS%20interface%20clean%20white%20background&width=600&height=800&seq=feature-speed-test-001&orientation=portrait" 
                  alt="Speed Test"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-white rounded-lg px-4 py-2">
                  <span className="text-sm font-medium text-gray-900">Speed Test</span>
                </div>
              </div>
              <div className="col-span-1 relative rounded-2xl overflow-hidden h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20app%20interface%20showing%20storage%20cleanup%20options%20with%20colorful%20category%20icons%20and%20clean%20button%20modern%20minimalist%20design%20white%20background&width=600&height=600&seq=feature-cleaner-interface-001&orientation=squarish" 
                  alt="Cleaner Interface"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-white rounded-lg px-4 py-2">
                  <span className="text-sm font-medium text-gray-900">Smart Clean</span>
                </div>
              </div>
              <div className="col-span-1 relative rounded-2xl overflow-hidden h-48">
                <img 
                  src="https://readdy.ai/api/search-image?query=storage%20analysis%20visualization%20showing%20pie%20chart%20with%20colorful%20segments%20representing%20different%20file%20types%20iOS%20app%20interface%20clean%20modern%20design&width=600&height=600&seq=feature-storage-analysis-001&orientation=squarish" 
                  alt="Storage Analysis"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-4 left-4 bg-white rounded-lg px-4 py-2">
                  <span className="text-sm font-medium text-gray-900">Analytics</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-24 px-8 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-5xl font-light text-gray-900 mb-6">
            Built with Modern iOS Technologies
          </h2>
          <p className="text-lg text-gray-500 mb-16">
            Swift, UIKit, and Core Frameworks
          </p>
          
          <div className="flex items-center justify-center gap-16 flex-wrap">
            <div className="w-20 h-20 flex items-center justify-center">
              <i className="ri-code-s-slash-line text-6xl text-gray-700"></i>
            </div>
            <div className="w-20 h-20 flex items-center justify-center">
              <i className="ri-smartphone-line text-6xl text-gray-700"></i>
            </div>
            <div className="w-20 h-20 flex items-center justify-center">
              <i className="ri-database-2-line text-6xl text-gray-700"></i>
            </div>
            <div className="w-20 h-20 flex items-center justify-center">
              <i className="ri-shield-check-line text-6xl text-gray-700"></i>
            </div>
            <div className="w-20 h-20 flex items-center justify-center">
              <i className="ri-cloud-line text-6xl text-gray-700"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="py-16 px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gray-900 rounded-[32px] p-16">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
              {/* Left Column */}
              <div className="md:col-span-5">
                <h2 className="text-5xl font-light text-white mb-6">Get in Touch</h2>
                <p className="text-base text-gray-400 leading-loose mb-16">
                  Have questions about our products or need support? We're here to help you with any inquiries.
                </p>
                
                <div className="mb-6">
                  <input 
                    type="email" 
                    placeholder="Your email address"
                    className="w-full bg-transparent border-b border-white/30 pb-3 text-white placeholder-gray-500 focus:outline-none focus:border-white transition-colors"
                  />
                </div>
                <button className="bg-white text-gray-900 px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap">
                  <span>Send Message</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>

              {/* Middle Column */}
              <div className="md:col-span-3">
                <h3 className="text-sm uppercase tracking-widest text-gray-400 mb-6">Quick Links</h3>
                <div className="space-y-4">
                  <button onClick={() => scrollToSection('about')} className="block text-base text-white hover:text-gray-300 transition-colors underline cursor-pointer whitespace-nowrap">About Us</button>
                  <button onClick={() => scrollToSection('products')} className="block text-base text-white hover:text-gray-300 transition-colors underline cursor-pointer whitespace-nowrap">VPN Product</button>
                  <button onClick={() => scrollToSection('products')} className="block text-base text-white hover:text-gray-300 transition-colors underline cursor-pointer whitespace-nowrap">Cleaner Product</button>
                  <button onClick={() => scrollToSection('contact')} className="block text-base text-white hover:text-gray-300 transition-colors underline cursor-pointer whitespace-nowrap">Support</button>
                </div>
              </div>

              {/* Right Column */}
              <div className="md:col-span-4">
                <h3 className="text-sm uppercase tracking-widest text-gray-400 mb-6">Contact Information</h3>
                <div className="space-y-4 text-base">
                  <div>
                    <p className="text-gray-400 mb-1">Address</p>
                    <p className="text-white">TIRANES L.14</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">City</p>
                    <p className="text-white">DURRES</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Zip</p>
                    <p className="text-white">2000</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Country</p>
                    <p className="text-white">Albania</p>
                  </div>
                  <div className="pt-6">
                    <p className="text-gray-400 mb-1">Email</p>
                    <p className="text-white">help@stellashpk.net</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1">Number</p>
                    <p className="text-white">+1 5186847324</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-white/20 pt-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-6">
                  <span className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">Twitter</span>
                  <span className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">LinkedIn</span>
                  <span className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">GitHub</span>
                </div>
                <div className="text-center">
                  <p>© 2025 STELLA SH.P.K. All rights reserved</p>
                </div>
                <div className="flex items-center gap-6">
                  <span className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</span>
                  <span className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">Terms of Service</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Link Footer */}
      <footer className="py-6 px-8 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto text-center">
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
